/**
 * Firebase-backed account lifecycle and privacy service (§15).
 *
 * A thin client over the account callables. Every rule — ownership, what is erased,
 * what an export contains — is enforced server-side; nothing here is a control. The
 * export is returned as a plain object for the caller to turn into a download.
 */

import { getFunctions, httpsCallable } from "firebase/functions";
import { REGION } from "../../config/regions.js";

/**
 * Turn a callable rejection into a message safe to show a user.
 *
 * @param {unknown} error
 * @returns {Error}
 */
function toUserError(error) {
  const message =
    error && typeof error === "object" && "message" in error && typeof error.message === "string"
      ? error.message
      : "";
  const cleaned = message.replace(/^[a-z-]+:\s*/i, "").trim();
  return new Error(
    cleaned.length > 0 ? cleaned : "We could not reach KinCode. Check your connection and retry.",
  );
}

/**
 * @param {import("./app.js").FirebaseContext} context
 * @returns {import("../interfaces.js").AccountService}
 */
export function createFirebaseAccountService(context) {
  const functions = getFunctions(context.app, REGION);

  /**
   * @template T
   * @param {string} name
   * @returns {(payload?: unknown) => Promise<T>}
   */
  function callable(name) {
    const fn = httpsCallable(functions, name);
    return async (payload = {}) => {
      try {
        const result = await fn(payload);
        return /** @type {T} */ (result.data);
      } catch (error) {
        throw toUserError(error);
      }
    };
  }

  const callRequestDeletion = callable("requestAccountDeletion");
  const callCancelDeletion = callable("cancelAccountDeletion");
  const callExport = callable("exportAccountData");
  const callAccept = callable("recordPolicyAcceptance");

  return {
    async requestDeletion() {
      return /** @type {{ status: string, deletesAt: number }} */ (await callRequestDeletion());
    },

    async cancelDeletion() {
      await callCancelDeletion();
    },

    async exportData() {
      return /** @type {object} */ (await callExport());
    },

    async acceptPolicies(versions) {
      await callAccept(versions);
    },
  };
}
